'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { REWARDS_ART } from '@/assets/rewards';
import { leaderboardHref } from '@/lib/components/RewardsHub/routes';
import { TRADE_ROUTE } from '@/lib/constants/routing';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { CardFooter } from './CardFooter';
import { CardHeader } from './CardHeader';
import {
  type BoardSegment,
  LEADERBOARD_TOP_COUNT,
  OTHER_SEGMENT,
  SIDEBAR_CARD_CLASS,
} from './constants';
import { LeaderRow, LeaderRowSkeleton, OwnRow } from './LeaderboardRows';
import { SidebarLink } from './SidebarLink';
import { useLeaderboardBoards } from './useLeaderboardBoards';
import { useVisitor } from './useVisitor';

/** The prediction board's way onto the board is the markets grid below on this page. */
const MARKETS_ANCHOR = '#all-markets';
const SKELETON_PLACES = Array.from({ length: LEADERBOARD_TOP_COUNT }, (_, i) => i + 1);
const GLOW_SIZE = { width: 2196, height: 1316 };

/**
 * The blue haze at the card's foot (Figma "Gradient V37"): the same blurred blob
 * twice, the second added on top for the bright core, both turned upside down.
 */
function CardGlow() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute top-[174px] left-[-20.89px] flex h-[790.85px] w-[1678.61px] items-center justify-center"
    >
      <div className="flex-none rotate-[-177.14deg] skew-x-[-3.92deg]">
        <div className="relative h-[716.31px] w-[1596.06px] opacity-20">
          <div className="absolute inset-[-41.88%_-18.8%]">
            <Image
              src={REWARDS_ART['leaderboard-card-glow.svg']}
              alt=""
              {...GLOW_SIZE}
              className="block size-full max-w-none"
            />
          </div>
          <div className="absolute inset-[-41.88%_-18.8%] mix-blend-plus-lighter">
            <Image
              src={REWARDS_ART['leaderboard-card-glow-light.svg']}
              alt=""
              {...GLOW_SIZE}
              className="block size-full max-w-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface FooterProps {
  segment: BoardSegment;
  isGuest: boolean;
  /** The signed-in player has a place on this board. */
  hasStanding: boolean;
}

/** Guests are asked to sign up; a player without a place this month, to trade. */
function LeaderboardFooter({ segment, isGuest, hasStanding }: FooterProps) {
  const t = useTranslations('home.leaderboard');
  const tNav = useTranslations('nav');

  if (isGuest) {
    return (
      <CardFooter
        className="pt-3"
        text={t('signUpPitch')}
        action={
          <Button asChild appearance="accent" size="S" className="min-w-20 shrink-0">
            <SidebarLink href="/auth/signup">{t('signUp')}</SidebarLink>
          </Button>
        }
      />
    );
  }
  if (hasStanding) return null;
  return (
    <CardFooter
      className="pt-3"
      text={t('tradePitch')}
      action={
        <Button asChild appearance="accent" size="S" className="min-w-20 shrink-0">
          {segment === 'crypto' ? (
            <SidebarLink href={TRADE_ROUTE}>{tNav('trade')}</SidebarLink>
          ) : (
            <a href={MARKETS_ANCHOR}>{tNav('trade')}</a>
          )}
        </Button>
      }
    />
  );
}

interface LeaderboardCardProps {
  /** The request carried a session cookie. */
  hasSession: boolean;
}

/**
 * Leaderboard (Figma 15001:34553 / 15001:34687): this month's top three by
 * trading volume on one board, switched between prediction markets and crypto
 * trading; a signed-in player's own place under them. The swap is offered only
 * to a board with leaders, so it never empties the card.
 */
export function LeaderboardCard({ hasSession }: LeaderboardCardProps) {
  const [chosenSegment, setChosenSegment] = useState<BoardSegment>();
  const [revealRows, setRevealRows] = useState(false);
  const t = useTranslations('home.leaderboard');
  const tBoard = useTranslations('rewardsHub.leaderboard');
  const { isSignedIn, isGuest } = useVisitor(hasSession);
  const { boards, openingSegment } = useLeaderboardBoards();

  const segment = chosenSegment ?? openingSegment;
  const { data: board, isLoading, leaders } = boards[segment];
  // Rows that replace a skeleton or a swapped board fade up in a stagger; the server-rendered board just appears.
  if (isLoading && !revealRows) setRevealRows(true);
  const segmentLabel: Record<BoardSegment, string> = {
    prediction: tBoard('segments.prediction'),
    crypto: tBoard('segments.crypto'),
  };
  const otherSegment = OTHER_SEGMENT[segment];
  const canSwitch = boards[otherSegment].leaders.length > 0;
  const me = board?.entries.find((entry) => entry.isMe);
  const ownRow = me && me.position > LEADERBOARD_TOP_COUNT ? me : undefined;
  const handleSwitch = () => {
    setChosenSegment(otherSegment);
    setRevealRows(true);
  };

  if (!isLoading && leaders.length === 0) return null;

  return (
    <section
      aria-label={t('title', { board: segmentLabel[segment] })}
      className={cn(SIDEBAR_CARD_CLASS, 'gap-3')}
    >
      <CardGlow />
      <CardHeader
        title={
          <>
            <span className="truncate">{t('title', { board: segmentLabel[segment] })}</span>
            {canSwitch ? (
              <button
                type="button"
                onClick={handleSwitch}
                aria-label={t('switchTo', { board: segmentLabel[otherSegment] })}
                className="flex shrink-0 cursor-pointer rounded-[4px] text-v2-text-secondary outline-none transition-colors duration-150 hover:text-v2-text-primary focus-visible:ring-2 focus-visible:ring-v2-bg-blue-50"
              >
                <Icon name="SwapVertical" size={16} />
              </button>
            ) : null}
          </>
        }
        href={leaderboardHref(segment)}
        linkLabel={t('open')}
      />
      <div className="relative flex flex-col gap-2">
        <ol className="flex flex-col gap-1">
          {isLoading
            ? SKELETON_PLACES.map((place) => (
                <li key={place}>
                  <LeaderRowSkeleton place={place} />
                </li>
              ))
            : leaders.map((entry, index) => (
                <li key={entry.userId}>
                  <LeaderRow entry={entry} reveal={revealRows} order={index} />
                </li>
              ))}
        </ol>
        {ownRow ? (
          <OwnRow entry={ownRow} reveal={revealRows} order={LEADERBOARD_TOP_COUNT} />
        ) : null}
      </div>
      {isLoading ? null : (
        <LeaderboardFooter
          segment={segment}
          isGuest={isGuest}
          hasStanding={!isSignedIn || Boolean(board?.hasStanding)}
        />
      )}
    </section>
  );
}
